import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, Tag, Filter, Search } from "lucide-react";
import { BLOGS } from "../data/blogs";

const CATEGORIES = ["All", "AI Research", "ML System Design", "Agentic Systems", "Voice AI"];

export default function Blogs() {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return BLOGS.filter((b) => {
      if (filter !== "All" && b.category !== filter) return false;
      if (q === "") return true;
      const haystack = [
        b.title,
        b.excerpt,
        b.category,
        ...(b.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [filter, search]);

  const loading = false;

  return (
    <div data-testid="blogs-page" className="bg-[#F5F5F0] min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="border-b border-[#D4D4D4] pb-12 mb-12">
          <p className="section-tag mb-3">&gt; BLOGS.DISPATCH</p>
          <h1 className="font-pixel text-3xl md:text-5xl text-[#0A0A0A] leading-tight mb-4">
            FROM THE LAB
          </h1>
          <p className="font-terminal text-sm text-[#0A0A0A]/50 max-w-2xl leading-relaxed">
            Deep dives into AI research, ML system design, agentic architectures, and the future of intelligent automation - straight from the Melonai research team.
          </p>
        </div>

        {/* Filters + Search */}
        <div className="flex flex-col lg:flex-row gap-4 mb-10">
          <div className="flex flex-wrap gap-2 items-center flex-1">
            <Filter size={14} className="text-[#0A0A0A]/30 self-center mr-2" />
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`blog-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-arcade text-base uppercase tracking-wide px-4 py-2 border transition-colors duration-150 ${
                  filter === cat
                    ? "bg-[#4ADE80] text-black border-[#4ADE80]"
                    : "bg-transparent text-[#0A0A0A]/50 border-[#C8C8C8] hover:border-[#4ADE80] hover:text-[#15803D]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative lg:w-[300px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40 pointer-events-none" />
            <input
              type="text"
              placeholder="search blogs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="blogs-search"
              className="w-full font-terminal text-sm border border-[#C8C8C8] bg-[#F5F5F0] pl-9 pr-3 py-2 placeholder:text-[#0A0A0A]/40 focus:outline-none focus:border-[#4ADE80] focus:bg-white"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border-2 border-[#E5E5E5] h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((blog) => (
              <Link
                key={blog.id}
                to={`/blogs/${blog.slug}`}
                className="block"
              >
              <article
                data-testid={`blog-${blog.id}`}
                className="group bg-white border-2 border-[#D4D4D4] card-hover overflow-hidden flex flex-col h-full"
              >
                <div className="relative h-44 overflow-hidden bg-[#F5F5F0] border-b-2 border-[#15803D]/15 flex items-center justify-center">
                  <img
                    src={blog.image_url}
                    alt={blog.title}
                    className="w-full h-full object-cover [image-rendering:pixelated]"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="font-arcade text-base text-[#15803D] border border-[#15803D] bg-white/85 px-2 py-1 uppercase">
                      {blog.category}
                    </span>
                  </div>
                  {blog.status === "coming-soon" && (
                    <div className="absolute top-3 right-3 bg-white/90 border border-[#BE185D] px-2 py-1">
                      <span className="font-arcade text-xs text-[#BE185D] uppercase">SOON</span>
                    </div>
                  )}
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-4 mb-3 text-[#0A0A0A]/30">
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      <span className="font-terminal text-xs">{blog.read_time} min read</span>
                    </span>
                    <span className="font-terminal text-xs">
                      {new Date(blog.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>

                  <h2 className="font-pixel text-xs text-[#0A0A0A] leading-snug mb-3 flex-1">
                    {blog.title}
                  </h2>

                  <p className="font-terminal text-xs text-[#0A0A0A]/50 leading-relaxed mb-4 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {blog.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="tag-chip text-sm flex items-center gap-1">
                        <Tag size={9} /> {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-end border-t border-[#E5E5E5] pt-4">
                    <span className="font-arcade text-sm text-[#15803D] group-hover:underline uppercase">
                      READ →
                    </span>
                  </div>
                </div>
              </article>
              </Link>
            ))}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <p className="font-arcade text-xl text-[#0A0A0A]/30 text-center py-20">
            No posts match.
          </p>
        )}
      </div>
    </div>
  );
}
